import translate from '@translate';
import React, { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ReceivingType } from 'src/types/receiving';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { checkPermission, useCheckPermission } from 'src/utils/checkPermission';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import { EditKeysType, EDIT_KEYS, PROPERTY_EDIT_KEYS, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import RenderComponents from './RenderComponents';
import {
    clearReceivingDetail,
    createReceivingDetailFetch,
    editReceivingDetailEshopNoteFetch,
    getReceivingDetailChangelogFetch,
    getReceivingDetailFetch,
    setReceivingDetailData,
    updateReceivingDetailFetch,
    patchReceivingDetailFetch,
} from 'src/resources/receivingDetail/reducer';
import ConfirmReceiving from './Confirm';
import { buildLockedReceivingPatchPayload, getAllowedStatuses, normalizeReceivingFiles } from './functions';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { receivingApiToEditData } from 'src/utils/receivingFiles';
import {
    buildStatusCancelPatchPayload,
    isCancelStatusChange,
    RECEIVING_CANCEL_STATUS,
} from 'src/utils/cancelStatus';
import api from '@api';
import dowloadFileFromData from 'src/utils/dowloadFileFromData';

export interface ReceivingDetailContainerPropsType {
    confirm?: boolean;
}

type AsnPdfFormat = 'a4' | 'a6';

const ASN_LABEL_FORMAT_OPTIONS = [
    { value: 'a4', label: translate('receivingDetail.asnLabel.format.a4') },
    { value: 'a6', label: translate('receivingDetail.asnLabel.format.a6') },
];

const initData = { PRODUCTS: [], PACKAGINGS: [], FILES: [] };
const ReceivingDetailContainer: FC<ReceivingDetailContainerPropsType> = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idReceiving } = useParams();
    const location = useLocation();

    const newReceiving = !idReceiving;

    const formRef = useRef<HTMLFormElement>(null);
    const productsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.receivingDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.receivingDetail.changelog);
    const loading = useSelector((state: RootStoreType) => state.receivingDetail.loading);

    const receivingStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingStatusCodes);
    const receivingStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingStatusCodes
    );
    const receivingUrgentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingUrgentCodes);
    const receivingUrgentCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingUrgentCodes
    );
    const receivingDeliveryTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.receivingDeliveryTypeCodes
    );
    const receivingDeliveryTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingDeliveryTypeCodes
    );

    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const productTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.productTypeCodes);
    const receivingPalletCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingPalletCodes);
    const receivingPalletCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingPalletCodes
    );
    const receivingPackingTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.receivingPackingTypeCodes
    );
    const receivingPackingTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingPackingTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const driverCompanyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.driverCompanyCodes);
    const driverCompanyCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.driverCompanyCodes
    );
    const receivingPriorityCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingPriorityCodes);
    const receivingPriorityCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.receivingPriorityCodes);
    const receivingTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingTypeCodes);
    const receivingTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.receivingTypeCodes);
    const incotermsCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.incotermsCodes);
    const incotermsCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.incotermsCodes);
    const lineStateCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.lineStateCodes);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);

    const [editData, setEditData] = useState<ReceivingType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>('detail');
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('RECEIVING.CREATE');
    const permissionUpdate = useCheckPermission('RECEIVING.UPDATE');
    const permissionConfirm = useCheckPermission('RECEIVING.CONFIRM');
    const permissionEditAll = useCheckPermission('RECEIVING.EDITALL');
    const permissionEditStatus = data?.STATUS && checkPermission(`RECEIVING.STATUS.EDIT.${data.STATUS.toUpperCase()}`);

    const receivingPropertyPermissions = useMemo(() => {
        const statusUpper = data?.STATUS?.toUpperCase();
        if (!statusUpper) {
            return {} as Record<string, boolean>;
        }
        return PROPERTY_EDIT_KEYS.reduce(
            (acc, key) => {
                acc[key] = checkPermission(`RECEIVING.PROPERTIES.${key}.EDIT.${statusUpper}`);
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [data?.STATUS]);

    const disableEdit = newReceiving ? !permissionCreate : !permissionEditStatus || !permissionUpdate;
    const isAttributeEditDisabled = (attributeKey: string) =>
        Boolean(disableEdit && !permissionEditAll && !receivingPropertyPermissions[attributeKey]);
    const canEditAnyReceivingPropertyInCurrentStatus = Object.values(receivingPropertyPermissions).some(Boolean);
    const allowedStatuses = getAllowedStatuses(receivingStatusCodes, receivingStatusCodesObject, data);

    const backUrl = '/receivings/';

    useEffect(() => {
        if (idReceiving) {
            dispatch(setLocationParams({ name: translate('receivingDetail.title', { idReceiving }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('receivingDetail.titleNew'), backUrl }));
        }
    }, [idReceiving, backUrl, locationHistory]);

    useEffect(() => {
        if (idReceiving) {
            const prefilled = (location.state as { prefilledDataFromImport?: ReceivingType } | undefined)
                ?.prefilledDataFromImport;
            if (prefilled && String(prefilled.ID) === String(idReceiving)) {
                dispatch(setReceivingDetailData(prefilled));
            } else {
                dispatch(getReceivingDetailFetch(idReceiving));
            }
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearReceivingDetail());
        };
    }, [idReceiving]);

    useEffect(() => {
        if (data) {
            setEditData(receivingApiToEditData(data));
        }

        if (!idReceiving && data?.ID) {
            navigate('/receivings/' + String(data.ID));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        if (!changelog && activeTab === 'history') {
            dispatch(getReceivingDetailChangelogFetch(idReceiving));
        }
    }, [activeTab, changelog]);

    // save edit data -start
    const editDataRef = useRef<ReceivingType>({});
    useEffect(() => {
        if (
            newReceiving &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/receivings') ||
                samePaths(locationHistory[1]?.pathname, '/receivings/new'))
        ) {
            setConfirmParams({
                title: translate('receivingDetail.confirmGetNewDataStorage'),
                content: translate('receivingDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                },
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);
    const setThisStorageData = (data: ReceivingType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (errorKey) {
            newErrors[errorKey] = error;
        } else {
            newErrors[key] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const downloadAsnLabel = (format: AsnPdfFormat) => {
        if (!idReceiving) {
            return;
        }
        const url = `api/secure/receivings/pdf-form/${idReceiving}?format=${format}`;
        api('get', url).then((response: { data: { data: { content: string; filename?: string } } }) => {
            const { content, filename } = response.data.data;
            const defaultFilename = format === 'a6' ? 'ASN_form_A6.pdf' : 'ASN_form.pdf';
            dowloadFileFromData('data:application/pdf;base64,', content, filename || defaultFilename);
        });
    };

    const openAsnLabelFormatDialog = () => {
        setConfirmParams({
            title: translate('receivingDetail.action.showPdfForm'),
            content: translate('receivingDetail.asnLabel.selectFormat'),
            options: ASN_LABEL_FORMAT_OPTIONS,
            optionsValue: 'a4',
            action: (selectedFormat?: AsnPdfFormat) => {
                downloadAsnLabel(selectedFormat || 'a4');
            },
        });
    };

    const handleSave = () => {
        if (disableEdit && !canEditAnyReceivingPropertyInCurrentStatus && !permissionEditAll) {
            dispatch(
                editReceivingDetailEshopNoteFetch({ idReceiving, data: { ESHOP_NOTE: editData.ESHOP_NOTE || '' } })
            );
            return false;
        }

        if (disableEdit && (canEditAnyReceivingPropertyInCurrentStatus || permissionEditAll)) {
            // PATCH: PUT validator requires the full receiving (UNITS_AMOUNT, STATUS, PRODUCTS)
            const partialPayload = buildLockedReceivingPatchPayload({
                editData,
                data,
                canEditFiles: Boolean(receivingPropertyPermissions.FILES || permissionEditAll),
            });
            if (Object.keys(partialPayload).length) {
                dispatch(patchReceivingDetailFetch({ idReceiving, data: partialPayload }));
            }
            return false;
        }

        if (isCancelStatusChange(editData.STATUS, newReceiving, RECEIVING_CANCEL_STATUS)) {
            dispatch(
                patchReceivingDetailFetch({
                    idReceiving,
                    data: buildStatusCancelPatchPayload(RECEIVING_CANCEL_STATUS),
                })
            );
            return false;
        }

        const newFormErrors: FormErrorsType = {};
        Object.assign(newFormErrors, checkFormValidity(formRef), checkFormValidity(productsFormRef));
        if (!editData?.PRODUCTS?.length) {
            newFormErrors.PRODUCTS = true;
        }

        if (!Object.keys(newFormErrors).length && !Object.values(editErrors).filter((value) => value).length) {
            const { RECEIVING_FILES: _rf, ...editPayload } = editData;
            const normalizedFiles = normalizeReceivingFiles(editData.FILES);
            const formatData = {
                ...editPayload,
                ...(normalizedFiles !== undefined ? { FILES: normalizedFiles } : {}),
                PRODUCTS: editData.PRODUCTS?.map((product) => {
                    const newProduct = { ...product };
                    if (newProduct.RECEIVINGS_PRODUCTS_ID && newProduct.RECEIVINGS_PRODUCTS_ID < 0) {
                        delete newProduct.RECEIVINGS_PRODUCTS_ID;
                    }
                    return newProduct;
                }),
                PACKAGINGS: editData.PACKAGINGS?.map((packaging) => {
                    const newPackaging = { ...packaging };
                    if (newPackaging.ID && newPackaging.ID < 0) {
                        delete newPackaging.ID;
                    }
                    return newPackaging;
                }),
            };
            if (newReceiving) {
                dispatch(createReceivingDetailFetch(formatData));
            } else {
                dispatch(updateReceivingDetailFetch({ idReceiving, data: formatData }));
            }
            return false;
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
            setEditErrors({ ...newFormErrors, ...editErrors });
            return true;
        }

    };

    return (
        <div data-testid="receiving-detail">
            <TopBlock
                data={data}
                idReceiving={idReceiving}
                newReceiving={newReceiving}
                disableEdit={disableEdit}
                permissionConfirm={permissionConfirm}
                loading={!!loading}
                handleSave={handleSave}
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                showPdfForm={openAsnLabelFormatDialog}
            />

            {loading && <Loading />}
            <RenderComponents
                formRef={formRef}
                productsFormRef={productsFormRef}
                data={data}
                onChange={onChange}
                editData={editData}
                editErrors={editErrors}
                newReceiving={newReceiving}
                disableEdit={disableEdit}
                isAttributeEditDisabled={isAttributeEditDisabled}
                permissionEditAll={permissionEditAll}
                allowedStatuses={allowedStatuses}
                receivingStatusCodes={receivingStatusCodes}
                receivingStatusCodesObject={receivingStatusCodesObject}
                receivingUrgentCodes={receivingUrgentCodes}
                receivingUrgentCodesObject={receivingUrgentCodesObject}
                receivingDeliveryTypeCodes={receivingDeliveryTypeCodes}
                receivingDeliveryTypeCodesObject={receivingDeliveryTypeCodesObject}

                productTypeCodes={productTypeCodes}
                productTypeCodesObject={productTypeCodesObject}
                receivingPackingTypeCodes={receivingPackingTypeCodes}
                receivingPackingTypeCodesObject={receivingPackingTypeCodesObject}
                receivingPalletCodes={receivingPalletCodes}
                receivingPalletCodesObject={receivingPalletCodesObject}
                boolNumberCodes={boolNumberCodes}
                boolNumberCodesObject={boolNumberCodesObject}
                driverCompanyCodes={driverCompanyCodes}
                driverCompanyCodesObject={driverCompanyCodesObject}
                receivingPriorityCodes={receivingPriorityCodes}
                receivingPriorityCodesObject={receivingPriorityCodesObject}
                receivingTypeCodes={receivingTypeCodes}
                receivingTypeCodesObject={receivingTypeCodesObject}
                incotermsCodes={incotermsCodes}
                incotermsCodesObject={incotermsCodesObject}
                lineStateCodesObject={lineStateCodesObject}
                activeTab={activeTab}
                changelog={changelog}
                skladonUser={skladonUser}
            />
            {!!idReceiving && props.confirm && <ConfirmReceiving idReceiving={idReceiving} />}

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </div>
    );
};

export default ReceivingDetailContainer;
